import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material';
import { FieldValues, useController } from 'react-hook-form';

import { AutocompleteField } from 'common/forms/Field';
import {
    InputOptionCertificatesDocument,
    ICertificateWithScoringFragment,
} from 'graphql/types';
import { CertificateInfo } from 'training/components/CertificateInfo';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

const BASE_QUERY_VARS = {
    first: 50,
};

type TCertificateOption = ICertificateWithScoringFragment & {
    isExternalCertificate?: boolean | null;
};

interface IProps {
    allowExternalCertificates?: boolean;
    name: string;
}

export const CertificateSelectField = <TFieldValues extends FieldValues>({
    allowExternalCertificates = false,
    control: formControl,
    name,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control = asTypedControl<TCertificateOption>(name, formControl);

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    const [inputValue, setInputValue] = useState('');

    const [fetchCertificates, { data, loading, called }] = useLazyQuery(
        InputOptionCertificatesDocument,
        { fetchPolicy: 'no-cache' }
    );

    const { certificates: certificatesData } = data || {};

    const certificates =
        certificatesData?.edges
            ?.map((edge) => {
                if (
                    allowExternalCertificates ||
                    !edge?.node?.isExternalCertificate
                )
                    return edge?.node;

                return null;
            })
            .filter(Boolean) || [];
    const certificatesCount = certificatesData?.count || 0;

    const value = field.value;

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                fetchCertificates({
                    variables: {
                        ...BASE_QUERY_VARS,
                        q: inputValue || undefined,
                    },
                });
            }, 400),
        [fetchCertificates]
    );

    useEffect(() => {
        // The first query is done by opening, so we want to avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    const handleOpen = () => {
        if (!!certificates.length) return;

        fetchCertificates({
            variables: {
                ...BASE_QUERY_VARS,
                q: inputValue || undefined,
            },
        });
    };

    return (
        <Box sx={{ mb: 1 }}>
            <AutocompleteField
                {...other}
                {...field}
                error={!!error}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option.name}
                helperText={error?.message}
                InputProps={{
                    label: translate('certificate'),
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loading}
                loadingText={translate('loadingDots')}
                noOptionsText={
                    // loadingText is not always shown, so we need to fallback for no options text
                    !loading
                        ? translate('noOptionsText.certificates')
                        : translate('loadingDots')
                }
                options={certificates}
                renderOption={(props, option) => (
                    <li {...props}>
                        <Box sx={{ width: '100%' }}>
                            {option.name}
                            {allowExternalCertificates &&
                                option.isExternalCertificate && (
                                    <Box sx={{ mt: -0.5 }}>
                                        <Chip
                                            label={translate('externalChip')}
                                            size="small"
                                        />
                                    </Box>
                                )}
                        </Box>
                    </li>
                )}
                slotProps={{
                    listbox: {
                        component: ({
                            children,
                            ...other
                        }: React.ComponentProps<'ul'>) => (
                            <ul {...other}>
                                {children}
                                {certificatesCount > BASE_QUERY_VARS.first && (
                                    <Box
                                        className="MuiAutocomplete-option"
                                        component="li"
                                        key="hasMore"
                                        sx={{
                                            pointerEvents: 'none',
                                            opacity: 0.6,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                width: '100%',
                                                textAlign: 'center',
                                            }}
                                            variant="caption"
                                        >
                                            {translate(
                                                'moreResultsSelectSearch'
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </ul>
                        ),
                    },
                }}
                value={value || null}
                onChange={(_event, value) => {
                    field.onChange(value);
                }}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                        setInputValue(newInputValue);
                    }
                }}
                onOpen={handleOpen}
            />

            {!!value && (
                <Box sx={{ mt: 2 }}>
                    <CertificateInfo certificate={value} />
                </Box>
            )}
        </Box>
    );
};
